import React from 'react';
import moment from 'moment/moment';
import {
    convertHours,
    formatAMPM,
    formatAMPMNeW,
    formatServiceCodeLabel,
    hooksForTable, Images,
    manageStatus,
    manageStatusCircle,
    PermissionList,
    RolePermission,
} from 'utils';
import { DownloadInvoicePdf } from './downloadInvoicePdf';
import {
    ApptDetailRow,
    DateRow,
    DateRowStartEnd,
    DisplayIdRow,
    LinkRow,
    MinLoader,
    PriceRow, SimpleTooltip,
    StatusCard,
    TextRow,
    ToolInfo,
} from 'components';
import { defaultStatuses } from '../../../../pages/client/constants';

export function getInvoiceDetails(invoice) {
    const { dateRange, pdfDocument, client, status, totalHours, totalUnits, memberId, totalAMT, invoiceDate, term, paidAMT, dueDate } =
        invoice || {};
    const { addSignToValueFromStart, getValueByFixedNumber, handleCreatedAtDate } = hooksForTable;
    const early = handleCreatedAtDate(dateRange?.early);
    const latest = handleCreatedAtDate(dateRange?.latest);
    const dueAmount = invoice?.balance ?? 0;

    return [
        {
            detailText: 'Client:',
            detail: !!client ? (
                <LinkRow
                    name={`${client?.clientCode ? `(${client?.clientCode})` : ''} ${client?.clientFullName}`}
                    href={RolePermission([PermissionList.CLIENT_READ?.code]) ? `/client/${client?.clientId}` : ''}
                    checkPermission={client?.clientId ? RolePermission([PermissionList.CLIENT_READ?.code]) : null}
                />
            ) : (
                '--'
            ),
        },
        {
            detailText: 'Total Hours:',
            detail: convertHours(totalHours ? totalHours : 0),
        },
        {
            detailText: 'Invoice Date:',
            detail: <DateRow date={invoiceDate} />,
        },
        {
            detailText: 'Member ID:',
            detail: <TextRow name={memberId ? memberId : '--'} />,
        },
        {
            detailText: 'Total Units:',
            detail: <TextRow name={totalUnits || totalUnits === 0 ? totalUnits : '--'} />,
        },
        {
            detailText: 'Term:',
            detail: '30',
        },
        {
            detailText: 'DOS:',
            detail: <DateRowStartEnd start={early} end={latest} />,
        },
        {
            detailText: 'Total Amount:',
            detail: addSignToValueFromStart(getValueByFixedNumber(totalAMT)),
        },
        {
            detailText: 'Due Date:',
            detail: <DateRow date={dueDate} />,
        },
        {
            detailText: 'Status:',
            detail: (
                <div className="flex-align-center">
                    <p>{manageStatusCircle(status)}</p>
                    <p>{manageStatus(status)}</p>
                </div>
            ),
        },
        {
            detailText: 'Paid AMT:',
            detail: <PriceRow info={paidAMT} />,
        },
        {
            detailText: 'PDF Document:',
            detail: !!pdfDocument || !!'file_pdf.pdf' ? <DownloadInvoicePdf /> : null,
        },
        {
            detailText: 'AMT Due:',
            detail: <PriceRow info={dueAmount} />,
        },
        {
            detailText: 'Adjustments:',
            detail: <PriceRow info={invoice?.adjustmentsTotal || 0} />,
        },
    ];
}

export const invoiceDetailHead = [
    { name: '', title: 'ID', noSearch: true, custom: false },
    { name: '', title: 'Dos', noSearch: true, custom: false },
    { name: '', title: 'Service Code', noSearch: true, custom: false },
    { name: '', title: 'Description', noSearch: true, custom: false },
    { name: '', title: 'Hours', noSearch: true, custom: false },
    { name: '', title: 'Total Billed', noSearch: true, custom: false },
    { name: '', title: 'Copay', noSearch: true, custom: false },
    { name: '', title: 'Coins.', noSearch: true, custom: false },
    { name: '', title: 'Deduct.', noSearch: true, custom: false },
    { name: '', title: 'AMT Due', noSearch: true, custom: false },
];

export const invoiceDetailBody = [
    { rowText: (item) => <DisplayIdRow id={item?.displayId ? item?.displayId : '--'} /> },
    { rowText: (item) => <DateRow date={item?.dos} /> },
    { rowText: (item) => <TextRow name={formatServiceCodeLabel(item?.cptCode, item?.serviceTypeCode)} /> },
    { rowText: (item) => <TextRow name={renderDInvoiceBillDesc(item)} /> },
    { rowText: (item) => (item?.hours ? convertHours(item?.hours) : '--') },
    { rowText: (item) => <PriceRow info={item?.totalBilled} /> },
    { rowText: (item) => <PriceRow info={item?.copay} /> },
    { rowText: (item) => <PriceRow info={item?.coIns} /> },
    { rowText: (item) => <PriceRow info={item?.deductible} /> },
    { rowText: (item) => <PriceRow info={item?.clientBalance} /> },
];

const renderDInvoiceBillDesc = (item) => {
    if (item?.appts?.length) {
        return item?.appts
            ?.map((item) => `${item?.staffFullName} (${formatAMPMNeW(item?.startTime)} - ${formatAMPMNeW(item?.endTime)})`)
            .join(', ');
    } else {
        return '--';
    }
};

export const invoiceDetailTabsLabels = [
    { label: 'Details' },
    { label: 'Appointments' },
    { label: 'Transactions' },
    { label: 'Comments' },
    RolePermission([PermissionList.INVOICE_NOTE_READ?.code, PermissionList.INVOICE_NOTE_MANAGE?.code]) && { label: 'Notes' },
    RolePermission([PermissionList.INVOICE_HISTORY_READ?.code]) && { label: 'History' },
    RolePermission([PermissionList.INVOICE_FILE_MANAGE?.code, PermissionList.INVOICE_FILE_READ?.code]) && { label: 'Files' },
];

export const invoiceAppointmentsHead = [
    { name: '', title: 'ID', noSearch: true, custom: false },
    { name: '', title: 'Date', noSearch: true, custom: false },
    { name: '', title: 'Time', noSearch: true, custom: false },
    { name: '', title: 'Staff Member', noSearch: true, custom: false },
];

export const invoiceAppointmentsBody = [
    { rowText: (item) => <ApptDetailRow name={item?.displayId ? item.displayId : '--'} id={item?.id} showWidth={'100%'} /> },
    { rowText: (item) => <DateRow date={item?.startTime} /> },
    { rowText: (item) => <p className="schedule-time">{`${formatAMPM(item?.startTime)} - ${formatAMPM(item?.endTime)}`}</p> },
    {
        rowText: (item) => (
            <LinkRow
                name={item?.staffFullName ? item.staffFullName : '--'}
                href={RolePermission([PermissionList.STAFF_READ.code]) ? `/staff/${item?.staffId}` : ''}
                checkPermission={item?.staffId ? RolePermission([PermissionList.STAFF_READ.code]) : null}
            />
        ),
    },
];

export const invoiceTransactionsHead = [
    { name: '', title: 'Trans. ID', noSearch: true, custom: false },
    { name: '', title: 'Created Date', noSearch: true, custom: false },
    { name: '', title: 'Voided Date', noSearch: true, custom: false },
    { name: '', title: 'Total AMT', noSearch: true, custom: false },
    { name: '', title: 'Paid AMT', noSearch: true, custom: false },
    { name: '', title: 'AMT Due', noSearch: true, custom: false },
    { name: '', title: 'Status', noSearch: true, custom: false },
];

export const invoiceTransactionsBody = [
    { rowText: (item) => <DisplayIdRow id={item?.displayId} /> },
    { rowText: (item) => <DateRow date={item?.createdDate} /> },
    { rowText: (item) => (item?.voidedDate ? <DateRow date={item?.voidedDate} /> : '--') },
    { rowText: (item) => <PriceRow info={item?.totalAMT} /> },
    { rowText: (item) => <PriceRow info={item?.paidAMT} /> },
    { rowText: (item) => <PriceRow info={item?.amtDue} /> },
    {
        rowText: (item) => (
            <p className={item?.status === 'ACTIVE' ? 'active-status' : 'inactive-status'}>
                {item?.status === 'ACTIVE' ? 'Active' : 'Voided'}
            </p>
        ),
    },
];

export const INVOICE_TRANSACTIONS_TYPE = 'GET_INVOICE_TRANSACTION';

/** New InvoiceTxn table definitions */
export const INVOICE_TXN_LOAD_TYPE = 'GET_INVOICE_TXNS';

const txnTypeLabels = {
    PAYMENT: 'Payment',
    WRITE_OFF: 'Write-Off',
    ADJUSTMENT: 'Adjustment',
    DISCOUNT: 'Discount',
    PAYMENT_REVERSAL: 'Payment Reversal',
};

const txnTypeBadgeStyles = {
    PAYMENT: {
        color: '#027A48',
        background: '#ECFDF3',
    },
    WRITE_OFF: {
        color: '#B54708',
        background: '#FFFAEB',
    },
    ADJUSTMENT: {
        color: '#5925DC',
        background: '#F4F3FF',
    },
    DISCOUNT: {
        color: '#175CD3',
        background: '#EFF8FF',
    },
    PAYMENT_REVERSAL: {
        color: '#B42318',
        background: '#FEF3F2',
    },
};

const TxnTypeBadge = ({ type }) => {
    const label = txnTypeLabels[type] || type;
    const style = txnTypeBadgeStyles[type] || {
        color: '#6b7280',
        background: '#f3f4f6',
        border: '1.5px solid #6b7280',
    };
    return (
        <span
            style={{
                ...style,
                display: 'inline-block',
                padding: '3px 10px',
                borderRadius: '20px',
                fontSize: '14px',
                fontWeight: 600,
                lineHeight: '20px',
                whiteSpace: 'nowrap',
            }}>
            {label}
        </span>
    );
};

export const transactionTypeEnums = {
    PAYMENT: 'PAYMENT',
    WRITE_OFF: 'WRITE_OFF',
    ADJUSTMENT: 'ADJUSTMENT',
    DISCOUNT: 'DISCOUNT',
};

const typeFilterOptions = [
    { id: '', name: 'All' },
    { id: transactionTypeEnums.PAYMENT, name: 'Payment' },
    { id: transactionTypeEnums.DISCOUNT, name: 'Discount' },
    { id: transactionTypeEnums.WRITE_OFF, name: 'Write-off' },
    { id: transactionTypeEnums.ADJUSTMENT, name: 'Adjustment' },
];

export const invoiceTxnTableHead = [
    { name: '', title: 'Trans. ID', noSearch: true, custom: false },
    { name: '', title: 'Created Date', noSearch: true, custom: false },
    {
        title: 'Type',
        disabled: true,
        width: '140px',
        multiple: false,
        name: 'typeFilter',
        noSearch: true,
        filterList: typeFilterOptions,
    },
    { name: '', title: 'Amount', noSearch: true, custom: false },
    { name: '', title: 'Reason', noSearch: true, custom: false },
    { name: '', title: 'Note', noSearch: true, custom: false },
    { name: '', title: 'Created By', noSearch: true, custom: false },
    { name: '', title: 'Status', noSearch: true, custom: false },
    { name: '', title: 'Action', noSearch: true, custom: false },
];

export const invoiceTxnTableBody = (handleVoid) => [
    { rowText: (item) => <DisplayIdRow id={item?.displayId} /> },
    { rowText: (item) => <DateRow date={item?.createdDate} /> },
    { rowText: (item) => <TxnTypeBadge type={item?.type} /> },
    { rowText: (item) => <PriceRow info={item?.amount} /> },
    { rowText: (item) => <TextRow name={item?.reason} /> },
    { rowText: (item) => <TextRow name={item?.notes} /> },
    { rowText: (item) => <TextRow name={item?.creator} /> },
    { rowText: (item) => <StatusCard status={item?.status} /> },
    {
        rowText: (item) =>
            item?.status === 'ACTIVE' ? (
                <button
                    className="close-action-btn"
                    style={{ fontSize: 12, padding: '4px 8px', width: 50 }}
                    onClick={() => handleVoid(item)}>
                    Void
                </button>
            ) : (
                <div className="flex-align-center" style={{ gap: 8 }}>
                    <div style={{ marginTop: 3 }}>
                        <SimpleTooltip
                            title={
                                <div>
                                    {item?.voidedBy && <p>By: {item?.voidedBy}</p>}
                                    {item?.voidReason && <p>Reason: {item?.voidReason}</p>}
                                </div>
                            }
                            placement="top-start">
                            <img src={Images.toolInfoIcon} alt={'icon'} />
                        </SimpleTooltip>
                    </div>
                    <DateRow date={item?.voidedDate} />
                </div>
            ),
        notClickable: true,
    },
];

export const invoiceEnums = {
    OPEN: 'OPEN',
    CLOSED: 'CLOSED',
};
